import { CommonModule, ViewportScroller } from '@angular/common';
import { AfterContentInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ButtonsModule, KENDO_BUTTONS } from '@progress/kendo-angular-buttons';
import { KENDO_DIALOGS, WindowModule, WindowRef } from '@progress/kendo-angular-dialog';
import { DropDownsModule, KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { EditorModule, KENDO_EDITOR } from '@progress/kendo-angular-editor';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { KENDO_LABELS, LabelModule } from '@progress/kendo-angular-label';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { BehaviorSubject, Subscription } from 'rxjs';
import { LoggerService } from '../../shared/logger.service';
import { FormModes, StoryTypes } from '../../common/constants.lib';
import { AutoFocusDirective } from '../../directives/auto-focus.directive';
import {
  CasiStoryEstimate, CasiStoryPriority, CasiStoryStatus,
  CasiStoryType,
  CasiUserStory,
  ICasiOwner,
  User as UserModel
} from '../../models/models.lib';
import { IconName, IconPrefix, IconProp } from '@fortawesome/fontawesome-svg-core';
import { all } from '@awesome.me/kit-787edf9aa0/icons';
import { KENDO_ICONS } from '@progress/kendo-angular-icons';
import { KENDO_TOOLBAR } from '@progress/kendo-angular-toolbar';

@Component({
  selector: 'casi-user-story-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    RouterLink,
    RouterLinkActive,
    // WindowModule,
    // EditorModule,
    InputsModule,
    ButtonsModule,
    LabelModule,
    DropDownsModule,
    LayoutModule,
    AutoFocusDirective,
    KENDO_DIALOGS,
    KENDO_EDITOR,
    KENDO_DROPDOWNS,
    KENDO_LABELS,
    KENDO_ICONS,
    KENDO_BUTTONS,
    KENDO_TOOLBAR
  ],
  templateUrl: './user-story-modal.component.html',
  styleUrl: './user-story-modal.component.scss'
})
export class UserStoryModalComponent implements OnInit, OnDestroy, AfterContentInit {

  private _userStory = new BehaviorSubject<CasiUserStory | undefined>(undefined);
  @Input() set userStory(value: CasiUserStory | undefined) {
    this._userStory.next(value);
    if (value)
      this.userStoryForm.patchValue(value);
    else
      this.userStoryForm.reset();
  }
  get userStory() {
    return this._userStory.getValue();
  }

  get $userStory() {
    return this._userStory.asObservable();
  }

  @Input() winTitle: string = 'Title Not Set';

  private _storyTypes: CasiStoryType[] = [];
  @Input({ required: true }) set storyTypes(val: CasiStoryType[]) {
    this._storyTypes = val;
    this.setStoryIcons();
  }
  get storyTypes() {
    return this._storyTypes;
  } //storyTypes: StoryType[] = [];
  @Input({ required: true }) storyStatuses: CasiStoryStatus[] = [];
  @Input({ required: true }) storyPriorities: CasiStoryPriority[] = [];
  @Input({ required: true }) storyEstimates: CasiStoryEstimate[] = [];
  @Input({ required: true }) users: UserModel[] = [];

  // private _mode: FormModes = FormModes.NOTSET;
  // @Input({ required: true }) set mode(val: FormModes) {
  //   this._mode = val;
  // }
  // get mode(): FormModes {
  //   return this._mode;
  // };

  @Output() submitForm: EventEmitter<FormGroup> = new EventEmitter();
  @Output() closeForm: EventEmitter<void> = new EventEmitter();

  userStoryForm: FormGroup;

  public selectedType: number | undefined = 1;
  public selectTypeIconClass: string | undefined;
  public selectedPriority: string | undefined; // = StoryPriorities.None;
  public selectedRequester: number | undefined;
  public selectedStatus: number | undefined;
  public selectedEstimate: number | undefined;
  public selectedOwners: ICasiOwner[] = [];
  public iconList: IconProp | undefined;

  private _subs: Subscription[] = [];

  constructor(
    public faIcons: FaIconLibrary,
    private fb: FormBuilder,
    private logger: LoggerService,
    private scroller: ViewportScroller
  ) {
    faIcons.addIcons(...all);

    this.userStoryForm = this.fb.group(
      {
        id: [null, [Validators.required]],
        project_id: [null, [Validators.required]],
        project_name: [null, []],
        title: [null, [Validators.required]],
        description: [null, []],
        user_story_status_id: [null, [Validators.required]],
        user_story_status_description: [null, []],
        user_story_type_id: [null, [Validators.required]],
        user_story_type_description: [null, []],
        user_story_priority_id: [null, [Validators.required]],
        user_story_priority_description: [null, []],
        user_story_estimate_id: [null, [Validators.required]],
        user_story_estimate_description: [null, []],
        project_label_id: [null, []],
        project_label_description: [null, []],
        requested_by_user_id: [null, []],
        requested_by_user: [null, []],
        accepted_at: [null, []],
        deadline: [null, []],
        updated_by_user_id: [null, []],
        updated_by_user: [null, []],
        owned_by_users_json: [null, []],
        owned_by_users: [null, []]
      }
    );
  }

  //#region NG Event Handlers

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnInit...');
    this._subs.forEach(s => s.unsubscribe());
  }

  ngAfterContentInit(): void {
    this.logger.debug('ngAfterContentInit...');
    // this.scroller.scrollToPosition([10, 10]);
    setTimeout(() => {
      // document.body.scrollTo({ top: 0, behavior: 'smooth' });
      this.scroller.scrollToPosition([0, 0]);
      // this.pForm.nativeElement.scrollTop();
    }, 50);
  }

  //#endregion

  //#region Form Properties

  get f() {
    return this.userStoryForm.controls;
  }
  get id() {
    return this.userStoryForm.get('id');
  }
  get project_id() {
    return this.userStoryForm.get('project_id');
  }
  get titleIsDirty() {
    return this.userStoryForm.dirty;
  }
  get title() {
    return this.userStoryForm.get('title');
  }
  get description() {
    return this.userStoryForm.get('description');
  }
  get user_story_status_id() {
    return this.userStoryForm.get('user_story_status_id');
  }
  get user_story_status_id_value() {
    return this.userStoryForm.get('user_story_status_id')?.value;
  }
  get user_story_type_id() {
    return this.userStoryForm.get('user_story_type_id');
  }
  get user_story_type_id_value() {
    return this.userStoryForm.get('user_story_type_id')?.value;
  }
  get user_story_priority_id() {
    return this.userStoryForm.get('user_story_priority_id');
  }
  get user_story_priority_id_value() {
    return this.userStoryForm.get('user_story_priority_id')?.value;
  }
  get user_story_estimate_id() {
    return this.userStoryForm.get('user_story_estimate_id');
  }
  get user_story_estimate_id_value() {
    return this.userStoryForm.get('user_story_estimate_id')?.value;
  }
  get project_label_id() {
    return this.userStoryForm.get('project_label_id');
  }
  get requested_by_user_id() {
    return this.userStoryForm.get('requested_by_user_id');
  }
  get requested_by_user_id_value() {
    return this.userStoryForm.get('requested_by_user_id')?.value;
  }
  get accepted_at() {
    return this.userStoryForm.get('accepted_at');
  }
  get deadline() {
    return this.userStoryForm.get('deadline');
  }
  get updated_by_user_id() {
    return this.userStoryForm.get('updated_by_user_id');
  }

  //#endregion

  //#region Private functions

  private setStoryIcons(): any {
    this.logger.debug(`setStoryIcons...storyTypes:`, this.storyTypes);
    this.logger.debug(`setStoryIcons...selectedType`, this.selectedType);
    if (this.storyTypes) {
      let found = this.storyTypes.find(st => st.id === this.selectedType);
      if (found) {
        this.iconList = [('fad' as IconPrefix), (found.icon as IconName)];
        setTimeout(() => {
          this.selectTypeIconClass = found.iconClass;
          this.logger.debug(`setStoryIcons: selectTypeIcons`, this.selectTypeIconClass);
        });
      }
      // this.logger.debug(`setStoryIcons: selectTypeIcons`, this.selectTypeIconClass);
    }
  }

  //#endregion

}
